import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import {
  useTable,
  useRowSelect,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";
import {
  Button,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Link } from "react-router-dom";
import { Create, Delete } from "@material-ui/icons";
import apiHelper from "../../../../util/ApiHelper";
import { DatePicker } from "@material-ui/pickers";
import { toast } from "react-toastify";
import { CustomCheckbox, modal } from "../../../../util";

const FilterDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 20px;

  p {
    font-size: 13px;
    line-height: 15px;
    color: #306f9d;
    padding-left: 3px;
    padding-right: 3px;
    cursor: pointer;

    span {
      color: #333333;
    }

    &.active {
      font-weight: bold;
      color: #333333;
    }

    &:first-child {
      padding-left: 0;
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  input: {
    width: "100%",
  },
  margin: {
    marginBottom: "40px",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
    "& .MuiSelect-root": {
      paddingTop: "9px",
      paddingBottom: "9px",
    },
    "& input": {
      padding: "11px 14px",
    },
    "& label": {
      transform: "translate(14px, 12px) scale(1)",
    },
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <CustomCheckbox color="primary" ref={resolvedRef} {...rest} />
      </>
    );
  }
);

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <>
      <TextField
        id="search-field"
        label="Search News"
        variant="outlined"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      />
      <Button
        type="submit"
        variant="outlined"
        color="primary"
        onClick={() => {
          onChange(value);
        }}
      >
        Search
      </Button>
    </>
  );
}

function ImportButton(props) {
  const { disabled, onChangeHandler } = props;
  const ref = useRef();

  const reset = () => {
    console.log("here");
    ref.current.value = "";
  };

  return (
    <label htmlFor="import-data">
      <input
        id="import-data"
        type="file"
        hidden
        onChange={(event) => {
          onChangeHandler(event, reset);
        }}
        ref={ref}
      />
      <Button
        variant="contained"
        component="span"
        color="primary"
        disabled={disabled}
      >
        Import from .csv
      </Button>
    </label>
  );
}

function ImportImageButton(props) {
  const { disabled, onChangeHandler } = props;
  const ref = React.useRef();

  const reset = () => {
    console.log("here");
    ref.current.value = "";
  };

  return (
    <label htmlFor="import-image-data">
      <input
        id="import-image-data"
        type="file"
        hidden
        onChange={(event) => {
          onChangeHandler(event, reset);
        }}
        ref={ref}
        accept=".zip"
      />
      <Button
        variant="contained"
        component="span"
        color="primary"
        disabled={disabled}
      >
        Import images
      </Button>
    </label>
  );
}

export default function NewsListingPage({ disabled, ...props }) {
  const classes = useStyles();
  const [data, setData] = React.useState([]);
  const [allData, setAllData] = React.useState([]);
  const [filterPublished, setFilterPublished] = React.useState([]);
  const [filterDraft, setFilterDraft] = React.useState([]);
  const [filterStatus, setFilterStatus] = React.useState("all");

  const [bulkActions, setBulkActions] = React.useState("");
  const [selectedDate, handleDateChange] = React.useState(null);

  //for storing total count
  const [totalCount, setTotalCount] = React.useState(0);

  const getNews = () => {
    apiHelper.hknt.hkntGetPagesByCategory("news").then((response) => {
      setData(response.data.content);
      setAllData(response.data.content);
      setFilterPublished(
        response.data.content.filter((x) => x.status === "PUBLISHED")
      );
      setFilterDraft(response.data.content.filter((x) => x.status === "DRAFT"));
    });
  };

  useEffect(() => {
    getNews();
  }, []);

  const actions = [
    {
      value: "delete",
      label: "Delete",
    },
  ];

  const handleBulkActions = (event, actions) => {
    setBulkActions(event.target.value);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
      },
      {
        Header: "Author",
        accessor: "createBy",
      },
      {
        Header: "Status",
        accessor: "status",
      },
      {
        Header: "Last Update",
        accessor: "updateDate",
      },
      {
        Header: "Action",
        accessor: "",
      },
    ],
    [data]
  );

  const {
    getTableProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox
                {...{ ...getToggleAllRowsSelectedProps(), disabled }}
              />
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  //for getting total count
  const getTotalCount = () => {
    setPageSize(1);
  };
  useEffect(() => getTotalCount, [globalFilter, filterDraft, filterStatus]);
  useEffect(() => {
    if (pageSize === 1) {
      setTotalCount(pageOptions.length);
      setPageSize(10);
    }
  }, [pageSize]);

  const deletePage = async (id) => {
    const decision = await modal.fire({
      title: "Delete News",
      text: "Are you sure to delete this news?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hknt.hkntDeletePage(id);
        if (result.data.code === 0) {
          getNews();
          toast.dark("News deleted");
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const applyBulkAction = async () => {
    if (bulkActions === "delete") {
      const arg = [];
      selectedFlatRows.map((row) => {
        arg.push(apiHelper.hknt.hkntDeletePage(row.original.id));
      });

      const result = await Promise.all(arg);
      const sum = result
        .map((o) => o.data.code)
        .reduce((accumulator, currentValue) => accumulator + currentValue);

      if (sum === 0) {
        getNews();
        toast.dark(`${result.length} news deleted.`);
      }
    }
  };

  const filterByDate = () => {
    const date = new Date(selectedDate);
    let month = date.getMonth() + 1;
    month = month < 10 ? "0" + month : month;
    const date_value = date.getFullYear() + "-" + month + "-01";

    apiHelper.hknt
      .hkntGetPages({ category: "news", date: date_value })
      .then((response) => {
        setData(response.data.content);
        setAllData(response.data.content);
        setFilterPublished(
          response.data.content.filter((x) => x.status === "PUBLISHED")
        );
        setFilterDraft(
          response.data.content.filter((x) => x.status === "DRAFT")
        );
      });
  };

  const filterDataWithStatus = (status) => {
    setFilterStatus(status);

    switch (status) {
      case "published":
        setData(filterPublished);
        break;

      case "draft":
        setData(filterDraft);
        break;

      case "all":
      default:
        setData(allData);
        break;
    }

    getTotalCount();
  };

  const handleFileUpload = async (event, reset) => {
    event.preventDefault();
    const fd = new FormData();
    fd.append("file", event.target.files[0]);
    try {
      const result = await apiHelper.hknt.hkntImportPage(fd);
      if (0 === result?.data?.code) {
        toast.dark("Successfully imported.");
        getNews();
      }
    } catch (err) {
      console.log(err);
    } finally {
      reset();
    }
  };

  const handleImageUpload = async (event, reset) => {
    event.preventDefault();
    const fd = new FormData();
    fd.append("file", event.target.files[0]);
    try {
      const result = await apiHelper.hknt.hkntImportPageImage(fd);
      if (0 === result?.data?.code) {
        toast.dark("Images successfully imported.");
        getNews();
      }
    } catch (err) {
      console.log(err);
    } finally {
      reset();
    }
  };

  return (
    data && (
      <>
        <Typography variant="h4" gutterBottom>
          News
        </Typography>
        <FilterDiv style={{ marginTop: "20px" }}>
          <p
            className={filterStatus === "all" && "active"}
            onClick={() => filterDataWithStatus("all")}
          >
            All <span>({allData.length})</span>
          </p>
          |
          <p
            className={filterStatus === "published" && "active"}
            onClick={() => filterDataWithStatus("published")}
          >
            Published <span>({filterPublished.length})</span>
          </p>
          |
          <p
            className={filterStatus === "draft" && "active"}
            onClick={() => filterDataWithStatus("draft")}
          >
            {" "}
            Drafts <span>({filterDraft.length})</span>
          </p>
        </FilterDiv>
        <div className={classes.flexRow}>
          <div>
            <TextField
              id="bulk-actions"
              select
              label="Bulk Actions"
              value={bulkActions}
              onChange={handleBulkActions}
              variant="outlined"
              disabled={disabled}
            >
              {actions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              onClick={applyBulkAction}
              disabled={disabled}
            >
              Apply
            </Button>
            <DatePicker
              variant="inline"
              inputVariant="outlined"
              openTo="year"
              views={["year", "month"]}
              label="Year and Month"
              value={selectedDate}
              onChange={handleDateChange}
            />
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              onClick={filterByDate}
            >
              Filter
            </Button>
          </div>
          <div>
            {/* <ImportImageButton
              onChangeHandler={handleImageUpload}
              disabled={disabled}
            />
            <ImportButton
              onChangeHandler={handleFileUpload}
              disabled={disabled}
            /> */}
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to={`${props.match.url}/create`}
              disabled={disabled}
            >
              Add News
            </Button>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
          </div>
        </div>

        <Paper>
          <Table aria-label="simple table" component="ul" {...getTableProps()}>
            <TableHead component="div">
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      style={{ paddingTop: 6, paddingBottom: 6 }}
                    >
                      {column.render("Header")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody component="div">
              {page.map((row, index) => {
                prepareRow(row);
                const news = row.original;
                return (
                  <TableRow>
                    <TableCell style={{ paddingTop: 6, paddingBottom: 6 }}>
                      <IndeterminateCheckbox
                        {...{ ...row.getToggleRowSelectedProps(), disabled }}
                      />
                    </TableCell>
                    <TableCell
                      style={{
                        paddingTop: 6,
                        paddingBottom: 6,
                        color: "#306F9D",
                      }}
                      component="th"
                      scope="row"
                    >
                      <Link
                        style={{ color: "#306F9D", textDecoration: "none" }}
                        to={`${props.match.url}/${news.id}`}
                      >
                        {news.title}
                      </Link>
                    </TableCell>
                    <TableCell
                      style={{ width: 160, paddingTop: 6, paddingBottom: 6 }}
                    >
                      {news.createBy}
                    </TableCell>
                    <TableCell
                      style={{
                        width: 160,
                        paddingTop: 6,
                        paddingBottom: 6,
                        color: "#306F9D",
                      }}
                    >
                      {news.status.charAt(0).toUpperCase() +
                        news.status.slice(1).toLowerCase()}
                    </TableCell>
                    <TableCell
                      style={{ width: 200, paddingTop: 6, paddingBottom: 6 }}
                    >
                      {news.updateDate}
                    </TableCell>
                    <TableCell
                      style={{ width: 160, paddingTop: 6, paddingBottom: 6 }}
                    >
                      <Link to={`${props.match.url}/${news.id}`}>
                        <Create fontSize="small" style={{ color: "#CCCCCC" }} />
                      </Link>{" "}
                      {disabled || (
                        <Delete
                          fontSize="small"
                          style={{ color: "#CCCCCC", cursor: "pointer" }}
                          onClick={() => {
                            deletePage(news.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        <div className={classes.flexRow}>
          <div></div>
          <div className={classes.pagination}>
            Show rows:{" "}
            <TextField
              id="page-rows"
              select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
              variant="outlined"
              style={{ minWidth: "70px" }}
            >
              {[5, 10, 15, 20].map((pageSize) => (
                <MenuItem key={pageSize} value={pageSize}>
                  {pageSize}
                </MenuItem>
              ))}
            </TextField>
            Go to{" "}
            <TextField
              id="pagination-field"
              variant="outlined"
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
            />
            {pageIndex * pageSize + 1} to{" "}
            {(pageIndex + 1) * pageSize > totalCount
              ? totalCount
              : (pageIndex + 1) * pageSize}{" "}
            of {totalCount}
            <Button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="left"
            >
              {"<"}
            </Button>
            <Button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="right"
            >
              {">"}
            </Button>
          </div>
        </div>
      </>
    )
  );
}
